import { Trans } from "@lingui/macro";
import { Tag } from "antd";
import { AmountTdWidth } from "bukku/components/Table/Common/AmountTd";
import { DateWidth } from "bukku/components/Table/Common/CommonWidths";
import { store } from 'bukku/redux/store';
import dayjs from 'dayjs';
import queryString from "query-string";
import React from "react";
import { getSortOrder } from "../components/Columns/getSortOrder";
import BukkuTooltip from "../components/Tooltip";
import { CustomFieldShow } from "../enums/CustomFieldShow";

function getFieldList(show) {
    const state = store.getState();
    const fields = state.List.fields.transaction || [];

    if (show)
        return fields.filter(field => field[show]);
    return fields;
}

export const CustomFieldHelper = {

    getTransactionFields: transaction => {
        const fieldsById = {};
        if (transaction.fields) {
            transaction.fields.forEach(item => {
                fieldsById[item.field_id] = item;
            });
        }

        return fieldsById;
    },

    getContactFields: contact => {
        const fieldsById = {};
        if (contact.fields) {
            contact.fields.forEach(item => {
                fieldsById[item.field_id] = item;
            });
        }

        return fieldsById;
    },

    initFormData: transaction => {
        const fields = getFieldList();

        const formData = {};
        const transactionFields = transaction?.fields || [];
        transactionFields.forEach(({ field_id, value }) => {
            const name = `field_${field_id}`;
            const { data_type } = fields.find(field => field.id === field_id) || {};

            switch (data_type) {
                case "DATE": {
                    const fieldValue = value || undefined;
                    const valueAsDate = fieldValue ? dayjs(fieldValue) : undefined;
                    formData[name] = fieldValue && valueAsDate.isValid() ? valueAsDate : undefined;
                    break;
                }
                case "AMOUNT":
                    formData[name] = (value || value === 0) ? value : undefined;
                    break;
                default:
                    formData[name] = value || undefined;
                    break;
            }
        });
        return formData;
    },

    initFormContact: (formData, fields, contact) => {
        const fieldsById = CustomFieldHelper.getContactFields(contact);
        fields.contact.forEach(field => {
            const fieldId = field.id;
            const fullFieldId = `field_${fieldId}`;

            const fieldWithValue = fieldsById[fieldId];

            let fieldValue;
            if (field.data_type === "DATE") {
                fieldValue = fieldWithValue?.value || undefined;
                const valueAsDate = fieldValue ? dayjs(fieldValue) : undefined;
                // eslint-disable-next-line no-param-reassign
                formData[fullFieldId] = fieldValue && valueAsDate.isValid() ? valueAsDate : undefined;
            }
            else if (field.data_type === "AMOUNT") {
                fieldValue = (
                    fieldWithValue?.value ||
                    fieldWithValue?.value === 0 ?
                        fieldWithValue.value :
                        undefined
                );
                // eslint-disable-next-line no-param-reassign
                formData[fullFieldId] = fieldValue;
            }
            else {
                fieldValue = fieldWithValue?.value || undefined;
                // eslint-disable-next-line no-param-reassign
                formData[fullFieldId] = fieldValue;
            }
        });
    },

    prepareFormData: (rawFormData, transactionType) => {
        const keyword = CustomFieldShow[transactionType];
        const fields = getFieldList(keyword);

        if (fields.length) {
            const formData = { fields: [] };
            fields.forEach(({ id, data_type }) => {
                const name = `field_${id}`;
                const value = rawFormData[name];

                if (value || value === 0) {
                    const isMoment = value && data_type === 'DATE' && dayjs.isDayjs(value);
                    const parsedValue = isMoment ? value.format("YYYY-MM-DD") : value;
                    formData.fields.push({
                        field_id: id,
                        value: parsedValue,
                    });
                }
            });
            return formData;
        }
        return {};
    },

    prepareFormContact: (rawFormData, formData, fields, contact) => {
        const fieldsById = CustomFieldHelper.getContactFields(contact);
        if (fields.contact.length) {
            fields.contact.forEach(field => {
                const fieldId = field.id;
                const fullFieldId = `field_${fieldId}`;

                const formFieldValue = rawFormData[fullFieldId];
                const fieldWithValue = fieldsById[fieldId];

                if (formFieldValue || formFieldValue === 0) {
                    const parsedValue = (
                        formFieldValue &&
                        field.data_type === 'DATE' ?
                            formFieldValue.format("YYYY-MM-DD") :
                            formFieldValue
                    );
                    formData.fields.push({
                        id: fieldWithValue?.id || null,
                        field_id: field.id,
                        name: field.name,
                        value: parsedValue,
                    });
                }
            });
        }
    },

    normalizeData(newData, fields) {
        if ((fields || []).length) {
            // eslint-disable-next-line no-param-reassign
            newData.fields = fields.map(field => ({
                field_id: field.field_id || null,
                value: (field.value || field.value === 0) ? field.value : null,
            }));
        }
    },

    /**
     *
     * @param {Object[]} fields - The fields list item []
     * @returns Object for antd table's column
     */
    getColumns: (fields, noSorter = false) => {
        if (!fields)
            return [];

        const params = queryString.parse(window?.location?.search);

        return fields.map(field => {
            const {data_type} = field;
            const commonProps = {
                colKeyGroup: 'custom_fields',
                title: (
                    <span className="align-center ">
                        <span className="ellipsis-div">{field.name}</span>
                    </span>
                ),
                dataIndex: `field_${field.id}`,
                key: `field_${field.id}`,
                // backend search result
                ...(params && !noSorter ? {
                    sorter: true,
                    sortOrder: getSortOrder(`field_${field.id}`),
                } : {}),
            };
            switch (data_type) {
                case "AMOUNT":
                    return {
                        ...commonProps,
                        align: 'right',
                        render: text => text || '-',
                    };
                case "DATE":
                    return {
                        ...commonProps,
                        width: DateWidth + 20,
                        render: text => (text ? dayjs(text).format('DD/MM/YYYY') : '-'),
                    };
                case "TEXT":
                default:
                    return {
                        ...commonProps,
                        // sorter: (a, b) => (a[`field_${field.id}`] || '-')
                        //     .localeCompare(b[`field_${field.id}`] || '-'),

                        render: text => (
                            <div className="ellipsis-div">
                                <BukkuTooltip>{text || '-'}</BukkuTooltip>
                            </div>
                        ),
                    };
            }
        });
    },

    getColumnsBaseTable: fields => {
        const getCellText = props => {
            const {
                rowData,
                column,
            } = props;
            return rowData[column.dataIndex];
        };

        if (!fields)
            return [];

        return fields.map(field => {
            const {data_type} = field;
            const commonProps = {
                title: field.name,
                dataIndex: `field_${field.id}`,
                key: `field_${field.id}`,
                label: (
                    <>
                        <Tag style={{position: 'relative', top: -1}} color="blue">
                            <Trans>Custom</Trans>
                        </Tag>
                        {field.name}
                    </>
                ),
            };
            switch (data_type) {
                case "AMOUNT":
                    return {
                        ...commonProps,
                        align: 'right',
                        width: AmountTdWidth,
                        flexGrow: 14,
                        cellRenderer: props => getCellText(props) || '-',
                    };
                case "DATE":
                    return {
                        ...commonProps,
                        width: DateWidth + 20,
                        flexGrow: 14,
                        cellRenderer: props => {
                            const text = getCellText(props);
                            return text ? dayjs(text).format('DD/MM/YYYY') : '-';
                        },
                    };
                case "TEXT":
                default:
                    return {
                        ...commonProps,
                        width: 0,
                        flexGrow: 14,
                        cellRenderer: props => {
                            const text = getCellText(props);
                            return (
                                <div className="ellipsis-div">
                                    <BukkuTooltip>{text || '-'}</BukkuTooltip>
                                </div>
                            );
                        },
                    };
            }
        });
    },

    getCustomizedColumnOptions: fields => {
        if (!fields)
            return [];

        return fields.map(field => ({
            value: `field_${field.id}`,
            label: (
                <>
                    <Tag style={{position: 'relative', top: -1}} color="blue">
                        <Trans>Custom</Trans>
                    </Tag>
                    {field.name}
                </>
            ),
            is_custom: true,
        }));
    },

    /**
     * Extract field ids from Columns selected
     * ['date', 'number', 'field_6', 'field_5', 'field_1'] --> [6, 5, 1]
     * @param {String[]} columnsSelected eg: ['date', 'number', 'field_6', 'field_5', 'field_1']
     */
    getFieldIds: columnsSelected => {
        if (!columnsSelected)
            return [];

        return columnsSelected.filter(col => col.indexOf('field_') !== -1)
            .map(fieldKey => parseInt(fieldKey.replace('field_', ''), 10));
    },

    /**
     * Append 'field_' to array of ids selected
     * [6,5,1] -> ['field_6', 'field_5', 'field_1']
     * @param {String[]} fieldIds eg: [1,2,4,7]
     */
    getFieldKeys: fieldIds => {
        if (!fieldIds)
            return [];
        return fieldIds.map(id => `field_${id}`);
    },

    /**
     * [{field_id:1, value: 'abc'}, {field_id:2, value: 'xyz'}] --> {field_1: 'abc', field_2: 'xyz'}
     * @param {*} array
     */
    convertArrayToKeyValue: array => {
        const result = {};
        if (!array)
            return result;
        array.forEach(item => {
            result[`field_${item.field_id}`] = item.value;
        });
        return result;
    },

};
